import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { calculateStoredResult } from '../services/resultEngine'
import type { Answers, StoredResult } from '../types'
import { loadSession, saveSession } from './storage'
import { SessionContext, type SessionValue } from './session-context'

const questionCount = 28

export function SessionProvider({ children }: PropsWithChildren) {
  const [snapshot, setSnapshot] = useState(loadSession)

  useEffect(() => saveSession(snapshot), [snapshot])

  const activate = useCallback(
    () => setSnapshot((current) => ({ ...current, activated: true })),
    [],
  )

  const setAnswer = useCallback((questionId: string, optionId: string) => {
    setSnapshot((current) => ({
      ...current,
      answers: { ...current.answers, [questionId]: optionId },
      result: null,
    }))
  }, [])

  const setCurrentQuestion = useCallback((index: number) => {
    setSnapshot((current) => ({ ...current, currentQuestion: Math.max(0, index) }))
  }, [])

  const calculateResult = useCallback(async (answersOverride?: Answers) => {
    const answers = answersOverride ?? snapshot.answers
    const stored = await calculateStoredResult(answers)
    if (!stored) return null
    setSnapshot((current) => {
      if (!answersOverride && current.answers !== answers) return current
      return { ...current, answers, result: stored }
    })
    return stored
  }, [snapshot.answers])

  const loadSharedResult = useCallback((answers: Answers, result: StoredResult) => {
    setSnapshot({
      activated: true,
      answers,
      currentQuestion: questionCount - 1,
      result,
    })
  }, [])

  const restart = useCallback(() => {
    setSnapshot((current) => ({ ...current, answers: {}, currentQuestion: 0, result: null }))
  }, [])

  const value = useMemo<SessionValue>(() => {
    const answeredCount = Math.min(questionCount, Object.keys(snapshot.answers).length)
    return {
      ...snapshot,
      activate,
      setAnswer,
      setCurrentQuestion,
      calculateResult,
      loadSharedResult,
      restart,
      answeredCount,
      isComplete: answeredCount === questionCount,
    }
  }, [activate, calculateResult, loadSharedResult, restart, setAnswer, setCurrentQuestion, snapshot])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
